import {
  DownOutlined,
  MoreOutlined,
  RightOutlined,
  UpOutlined,
  WarningOutlined
} from "@ant-design/icons"
import { Badge, Button, Card, Dropdown, Flex, Switch, Tag, Typography } from "antd"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "react-router"
import { useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../../../../../constants/routes.ts"
import { useAppDispatch } from "../../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../../hooks/use-app-selector.ts"
import { budgetFormat } from "../../../../../../utils/number/budget-format.ts"
import { PROJECT_ENTITY_CATEGORIES_MAP } from "../../../constants/project-entity-category.ts"
import { selectProjectWritePermissions } from "../../../../helpers/project-permissions.selectors.ts"
import { typeLabel } from "../../../../helpers/repeated-project-type.ts"
import { fetchProjectMilestones } from "../../../../redux/project-milestones/project-milestones.thunks.ts"
import {
  fetchFinancialAlerts,
  fetchFinancialSnapshot,
  setFinancialAlertsMuted
} from "../../../../redux/project-finance/project-finance.thunks.ts"
import { projectSetProp } from "../../../../redux/project.slice.ts"
import { updateProject } from "../../../../redux/project.thunks.ts"
import { type FinancialAlert } from "./FinancialAlertBanner/FinancialAlertBanner.tsx"
import { UpdateContractValueModal } from "./FinancialAlertBanner/UpdateContractValueModal.tsx"
import {
  AMOUNT_FIELD,
  selectContributingMilestones
} from "./financial-alerts-milestones.ts"
import { groupFinancialAlerts } from "./financial-alerts-grouping.ts"

const { Text } = Typography

const CONTRACT_RULES = new Set([
  "contract_value_exceeded",
  "contract_expenses_exceeded"
])

const isExpenseRule = (rule: string) => rule === "contract_expenses_exceeded"

/**
 * The sidebar's Financial Alerts management area — collapsible, one compact
 * row per rule (planned+actual merged, per `groupFinancialAlerts`), read on
 * open. Distinct from the header's icon (a glance) and the Finance detail
 * drawer's markers: this is where a PM works an alert down to zero.
 *
 * Three independent states per rule, all component-local (not persisted):
 * - Read: seen, but the row stays — the badge only counts unread + active.
 * - Cleared: dismissed by the user; removed from the active list; does not
 *   touch financial data.
 * - Resolved: the engine itself stops reporting the rule on its next fetch
 *   (after an Update Contract, or any other financial change) — the group
 *   simply disappears, no client-side bookkeeping needed.
 *
 * Read/cleared are rule-keyed, so if a rule resolves and the same condition
 * later recurs, it is not still marked from before: an effect prunes both
 * sets down to whatever the latest fetch actually reports as active.
 *
 * The contract decision lives on each contributing milestone, not on the
 * alert as a whole — "Update Contract" adds only that milestone's amount to
 * the current contract (never the whole exposure), and "Keep Unchanged" only
 * acknowledges that one milestone. A milestone that gets included drops out
 * of the contributing set on its own once the refetched exposure shrinks
 * below it — nothing here removes it manually.
 *
 * The section header (title/count/chevron) always renders — only the
 * content collapses. Losing sight of "alerts exist" was a bug this fixes.
 */
export function FinancialAlertsPanel() {
  const projectId = useParams()?.id as string
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const alerts = useAppSelector((state) => state?.project?.financialAlerts)
  const snapshot = useAppSelector(
    (state) => state?.project?.financialTransactionSnapshot
  )
  const projectDetails = useAppSelector((state) => state?.project?.project)
  const milestones = useAppSelector(
    (state) => state?.project?.milestones?.results ?? []
  )
  const canUpdateContract = !!useAppSelector(selectProjectWritePermissions)
    ?.finances?.project_finance_edit
  const focusRule = useAppSelector(
    (state) => state?.project?.financialAlertFocusRule
  )

  // Two instances of one type share an id, and the bare-id entry answers for a
  // milestone that names none — which the backend files under the first.
  const projectTypeNameByKey = useMemo(() => {
    const byKey: Record<string, string> = {}

    for (const detail of projectDetails?.project_type_details ?? []) {
      const label = typeLabel(detail)
      if (detail.assignment_id) byKey[detail.assignment_id] = label
      if (!(detail.id in byKey) || detail.occurrence === 1) {
        byKey[detail.id] = label
      }
    }

    return byKey
  }, [projectDetails?.project_type_details])

  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const [collapsed, setCollapsed] = useState(false)
  const [expandedRule, setExpandedRule] = useState<string | null>(null)
  const [milestonesExpandedFor, setMilestonesExpandedFor] = useState<
    Set<string>
  >(new Set())
  const [readRules, setReadRules] = useState<Set<string>>(new Set())
  const [clearedRules, setClearedRules] = useState<Set<string>>(new Set())
  const [acknowledgedMilestones, setAcknowledgedMilestones] = useState<
    Set<string>
  >(new Set())
  const [contractAlert, setContractAlert] = useState<FinancialAlert | null>(
    null
  )
  // The specific milestone's contribution — not the alert's whole exposure —
  // is what "Update Contract" from a milestone row adds to the contract.
  const [pendingAmount, setPendingAmount] = useState<number | null>(null)

  useEffect(() => {
    if (projectId) void dispatch(fetchFinancialAlerts(projectId))
  }, [dispatch, projectId])

  // A generous single page — this only needs to rank the biggest
  // contributors, not enumerate every milestone in the project, so it
  // deliberately doesn't follow pagination the way the Milestones tab does.
  useEffect(() => {
    if (projectId) {
      void dispatch(
        fetchProjectMilestones({
          isMore: false,
          params: { lead: projectId, project_type: null, page: 1, page_size: 100 }
        })
      )
    }
  }, [dispatch, projectId])

  const groups = useMemo(
    () => groupFinancialAlerts(alerts?.alerts ?? []),
    [alerts]
  )

  // The engine, not this component, decides what's active — every fetch is
  // the truth. A rule absent here has resolved, so its read/cleared flags
  // are dropped: a later recurrence of the same rule is a new alert, unread.
  useEffect(() => {
    const activeRules = new Set(groups.map((g) => g.rule))

    setReadRules((current) => {
      const next = new Set([...current].filter((rule) => activeRules.has(rule)))
      return next.size === current.size ? current : next
    })
    setClearedRules((current) => {
      const next = new Set([...current].filter((rule) => activeRules.has(rule)))
      return next.size === current.size ? current : next
    })
  }, [groups])

  // The header's alert icon sets this instead of navigating away — reveal
  // the section, open the matching row, scroll it into view, and consume
  // the signal so clicking the same icon again still re-fires it.
  useEffect(() => {
    if (!focusRule) return
    if (!groups.some((g) => g.rule === focusRule)) return

    setCollapsed(false)
    setExpandedRule(focusRule)
    setReadRules((current) => new Set(current).add(focusRule))
    rowRefs.current[focusRule]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest"
    })
    dispatch(projectSetProp({ key: "financialAlertFocusRule", value: null }))
  }, [focusRule, groups, dispatch])

  const activeGroups = useMemo(
    () => groups.filter((g) => !clearedRules.has(g.rule)),
    [groups, clearedRules]
  )
  const unreadGroups = useMemo(
    () => activeGroups.filter((g) => !readRules.has(g.rule)),
    [activeGroups, readRules]
  )

  const toggleExpand = (rule: string) => {
    setExpandedRule((current) => (current === rule ? null : rule))
    setReadRules((current) => new Set(current).add(rule))
  }

  const handleClearAlert = (rule: string) => {
    setClearedRules((current) => new Set(current).add(rule))
    setExpandedRule((current) => (current === rule ? null : current))
  }

  const toggleMilestones = (rule: string) => {
    setMilestonesExpandedFor((current) => {
      const next = new Set(current)
      if (next.has(rule)) next.delete(rule)
      else next.add(rule)
      return next
    })
  }

  const goToMilestone = () => {
    if (projectId) navigate(RoutesPaths.project.view(projectId).milestones)
  }

  const isExpenseSide = contractAlert ? isExpenseRule(contractAlert.rule) : false

  const currentValue = Number(
    (isExpenseSide
      ? projectDetails?.planned_payable
      : projectDetails?.sales_price) ?? 0
  )
  // Prefilled as current contract + this one milestone's amount — never the
  // alert's whole exposure — but still editable, same as every other
  // contract-value confirmation in this app.
  const suggestedValue = currentValue + (pendingAmount ?? 0)

  const handleUpdateContract = useCallback(
    async (value: number) => {
      if (!projectId || !contractAlert) return
      await dispatch(
        updateProject({
          id: projectId,
          [isExpenseRule(contractAlert.rule)
            ? "planned_payable"
            : "sales_price"]: String(value)
        })
      ).unwrap()
      setContractAlert(null)
      setPendingAmount(null)
      await Promise.all([
        dispatch(fetchFinancialAlerts(projectId)).unwrap(),
        dispatch(fetchFinancialSnapshot(projectId)).unwrap()
      ])
    },
    [dispatch, contractAlert, projectId]
  )

  const handleOpenMilestoneUpdate = (alert: FinancialAlert, amount: number) => {
    setContractAlert(alert)
    setPendingAmount(amount)
  }

  const handleKeepMilestoneUnchanged = (rule: string, milestoneId: string) => {
    setAcknowledgedMilestones((current) =>
      new Set(current).add(`${rule}:${milestoneId}`)
    )
  }

  const handleToggleMute = useCallback(() => {
    if (!projectId) return
    void dispatch(
      setFinancialAlertsMuted({ projectId, muted: !alerts?.muted })
    )
  }, [dispatch, projectId, alerts?.muted])

  if (!activeGroups.length) return null

  return (
    <>
      <Card
        size="small"
        classNames={{ header: "!py-1", body: collapsed ? "!p-0" : "p-2" }}
        title={
          <Flex
            justify="space-between"
            align="center"
            className="cursor-pointer"
            onClick={() => setCollapsed((c) => !c)}
          >
            <Text strong>Financial Alerts</Text>
            <Flex align="center" gap={12}>
              {canUpdateContract && (
                <Flex
                  align="center"
                  gap={4}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Text type="secondary" className="text-xs">
                    Notifications
                  </Text>
                  <Switch
                    size="small"
                    checked={!alerts?.muted}
                    onChange={handleToggleMute}
                  />
                </Flex>
              )}
              <Badge
                count={unreadGroups.length}
                color={
                  unreadGroups.some((g) => g.severity === "critical")
                    ? "red"
                    : "orange"
                }
              />
              {collapsed ? (
                <RightOutlined className="text-xs" />
              ) : (
                <DownOutlined className="text-xs" />
              )}
            </Flex>
          </Flex>
        }
      >
        {!collapsed && (
        <Flex vertical gap={4}>
          {activeGroups.map((group) => {
            const unread = !readRules.has(group.rule)
            const expanded = expandedRule === group.rule
            const canAct = canUpdateContract && CONTRACT_RULES.has(group.rule)
            const amountField = AMOUNT_FIELD[`${group.rule}:${group.primary.basis}`]

            const excess =
              amountField &&
              Number(
                (isExpenseRule(group.rule)
                  ? snapshot?.total_expenses?.[group.primary.basis]
                  : snapshot?.sales_price?.[group.primary.basis]) ?? 0
              ) -
                Number(
                  (isExpenseRule(group.rule)
                    ? projectDetails?.planned_payable
                    : projectDetails?.sales_price) ?? 0
                )

            const contributing = amountField
              ? selectContributingMilestones(milestones, amountField, excess)
              : []

            const milestonesExpanded = milestonesExpandedFor.has(group.rule)

            return (
              <div
                key={group.rule}
                ref={(el) => {
                  rowRefs.current[group.rule] = el
                }}
              >
                <Flex
                  justify="space-between"
                  align="center"
                  gap={8}
                  className="cursor-pointer rounded py-1 hover:bg-[var(--ant-color-fill-tertiary)]"
                  onClick={() => toggleExpand(group.rule)}
                >
                  <Flex align="center" gap={6} className="min-w-0">
                    <WarningOutlined
                      className={
                        group.severity === "critical"
                          ? "text-red-500"
                          : "text-orange-400"
                      }
                    />
                    <Text
                      className={`text-sm min-w-0 truncate ${unread ? "font-semibold" : ""}`}
                    >
                      {group.primary.message}
                    </Text>
                  </Flex>
                  <Flex align="center" gap={6} className="shrink-0">
                    {unread && (
                      <span className="inline-block size-1.5 rounded-full bg-blue-500" />
                    )}
                    <Text type="secondary" className="text-xs">
                      {group.members.length}
                    </Text>
                    {expanded ? (
                      <UpOutlined className="text-xs" />
                    ) : (
                      <RightOutlined className="text-xs" />
                    )}
                  </Flex>
                </Flex>

                <Text type="secondary" className="text-xs pl-5 block">
                  {group.primary.recommendation}
                </Text>

                {expanded && (
                  <Flex vertical gap={4} className="pl-5 mt-1">
                    {group.members.map((member) => {
                      const expense = isExpenseRule(member.rule)
                      const total = Number(
                        (expense
                          ? snapshot?.total_expenses?.[member.basis]
                          : snapshot?.sales_price?.[member.basis]) ?? 0
                      )
                      const contract = Number(
                        (expense
                          ? projectDetails?.planned_payable
                          : projectDetails?.sales_price) ?? 0
                      )

                      return (
                        <Text key={member.basis} type="secondary" className="text-xs">
                          {member.basis === "planned" ? "Planned" : "Actual"}:{" "}
                          <Text
                            type={total > contract ? "danger" : "secondary"}
                            className="text-xs"
                          >
                            {budgetFormat(total)}
                          </Text>{" "}
                          vs. contract {budgetFormat(contract)}
                        </Text>
                      )
                    })}

                    {contributing.length > 0 && (
                      <div>
                        <Flex
                          align="center"
                          gap={4}
                          className="cursor-pointer"
                          onClick={() => toggleMilestones(group.rule)}
                        >
                          <Text className="text-xs" underline>
                            Contributing milestones · {contributing.length}
                          </Text>
                          {milestonesExpanded ? (
                            <UpOutlined className="text-xs" />
                          ) : (
                            <DownOutlined className="text-xs" />
                          )}
                        </Flex>

                        {milestonesExpanded && (
                          <Flex vertical gap={2} className="mt-1">
                            {contributing.map(({ milestone, amount }) => {
                              const category =
                                PROJECT_ENTITY_CATEGORIES_MAP?.[
                                  milestone.milestone_category
                                ]
                              const projectTypeKey =
                                milestone.project_type_assignment ??
                                milestone.project_type
                              const projectTypeName = projectTypeKey
                                ? projectTypeNameByKey[projectTypeKey]
                                : undefined
                              const acknowledged = acknowledgedMilestones.has(
                                `${group.rule}:${milestone.id}`
                              )

                              return (
                                <Flex
                                  key={milestone.id}
                                  align="center"
                                  justify="space-between"
                                  gap={4}
                                  className="rounded px-1 py-0.5 hover:bg-[var(--ant-color-fill-tertiary)]"
                                >
                                  <Flex
                                    vertical
                                    gap={0}
                                    className="cursor-pointer min-w-0"
                                    onClick={goToMilestone}
                                  >
                                    <Text className="text-xs">
                                      {milestone.name}
                                    </Text>
                                    <Flex align="center" gap={4}>
                                      {projectTypeName && (
                                        <Text type="secondary" className="text-xs">
                                          {projectTypeName} ·
                                        </Text>
                                      )}
                                      <Tag
                                        style={category?.style}
                                        className="!m-0 !text-[10px] !leading-4 !py-0"
                                      >
                                        {milestone.milestone_category_display}
                                      </Tag>
                                      <Text type="secondary" className="text-xs">
                                        +{budgetFormat(amount)}
                                      </Text>
                                    </Flex>
                                  </Flex>

                                  {canAct &&
                                    (acknowledged ? (
                                      <Text type="secondary" className="text-xs shrink-0">
                                        Acknowledged
                                      </Text>
                                    ) : (
                                      <Dropdown
                                        trigger={["click"]}
                                        menu={{
                                          items: [
                                            {
                                              key: "update",
                                              label: "Update Contract",
                                              onClick: () =>
                                                handleOpenMilestoneUpdate(
                                                  group.primary,
                                                  amount
                                                )
                                            },
                                            {
                                              key: "keep",
                                              label: "Keep Unchanged",
                                              onClick: () =>
                                                handleKeepMilestoneUnchanged(
                                                  group.rule,
                                                  milestone.id
                                                )
                                            }
                                          ]
                                        }}
                                      >
                                        <Button
                                          size="small"
                                          type="text"
                                          className="shrink-0 !px-1"
                                          onClick={(e) => e.stopPropagation()}
                                          icon={<MoreOutlined />}
                                          aria-label={`Contract decision for ${milestone.name}`}
                                        />
                                      </Dropdown>
                                    ))}
                                </Flex>
                              )
                            })}
                          </Flex>
                        )}
                      </div>
                    )}

                    <Flex gap={8} className="mt-1">
                      <Button
                        size="small"
                        type="text"
                        onClick={() => handleClearAlert(group.rule)}
                      >
                        Clear Alert
                      </Button>
                    </Flex>
                  </Flex>
                )}
              </div>
            )
          })}
        </Flex>
        )}
      </Card>

      {contractAlert && (
        <UpdateContractValueModal
          open
          alert={contractAlert}
          currentValue={currentValue}
          suggestedValue={suggestedValue}
          onCancel={() => setContractAlert(null)}
          onSubmit={handleUpdateContract}
        />
      )}
    </>
  )
}
